import { useMemo } from 'react';
import { remapBooleanConditionReferencesUpwardInScope } from '../../../../../../../validator/utils/remapReferencesUpwardInScope';
import { IWidgetOption } from '../../../../../Widget';
import {
  IComponentOptions,
  WidgetOptionLogic,
} from '../../../../../WidgetOptionLogic';
import { useBooleanConditions } from '../../../../../hooks/useBooleanConditions';
import { IMultiSelectWidget } from '../../IMultiSelectWidget';
import { multiSelectOptionsObjectName } from '../../getMultiSelectOptionsObjectName';

interface IUseMultiSelectWidgetInput {
  widget: IMultiSelectWidget;
  name: string;
  preview?: boolean;
}

export interface IUseMultiSelectWidgetOptionsReturn {
  options: IWidgetOption[];
  componentOptions: IComponentOptions;
}

/**
 * Ottieniamo tutte le option e le relative condizioni di visibilità/abilitazione
 * per le multi-select (che sono gestite con un componente diverso rispetto alle
 * select singole, per via della struttura dati)
 */
export function useMultiSelectWidgetOptions({
  widget,
  name,
  preview,
}: IUseMultiSelectWidgetInput): IUseMultiSelectWidgetOptionsReturn {
  const [optionsName, parent] = useMemo(() => {
    const optionsName = multiSelectOptionsObjectName(name);
    return [optionsName, { code: optionsName }];
  }, [name]);

  const optionsWithConditions = useMemo(() => {
    return widget.options.map(option => ({
      option,
      visible: remapBooleanConditionReferencesUpwardInScope(
        parent,
        option.visible,
      ),
      enabled: remapBooleanConditionReferencesUpwardInScope(
        parent,
        option.type === 'divider' ? null : option.enabled,
      ),
    }));
  }, [widget.options, parent]);

  const visibles = useBooleanConditions(
    optionsWithConditions.map(o => o.visible),
    { name: `${optionsName}.childVirtualNode`, preview },
  );

  const enableds = useBooleanConditions(
    optionsWithConditions.map(o => o.enabled),
    { name: `${optionsName}.childVirtualNode`, preview },
  );

  return useMemo(() => {
    const options = widget.options
      .map((option, i) => ({
        ...option,
        disabled: !enableds[i].result,
      }))
      .filter((_, index) => visibles[index].result);

    return {
      options,
      componentOptions: WidgetOptionLogic.parseComponentOptions(options),
    };
  }, [widget.options, visibles, enableds]);
}
